import { Nullable } from './../../../../../member-portal/src/utils/types';
// axios配置  可自行根据项目进行更改，只需更改该文件即可，其他文件可以不动
// The axios configuration can be changed according to the project, just change the file, other files can be left unchanged

import type { AxiosInstance, AxiosResponse } from 'axios';
import { clone } from 'lodash-es';
import type { RequestOptions, Result } from '/#/axios';
import type { AxiosTransform, CreateAxiosOptions } from './axiosTransform';
import { VAxios } from './Axios';
import { checkStatus } from './checkStatus';
import { useGlobSetting } from '/@/hooks/setting';
import { useMessage } from '/@/hooks/web/useMessage';
import { RequestEnum, ResultEnum, ContentTypeEnum, ErrorCodeEnum } from '/@/enums/httpEnum';
import { isString } from '/@/utils/is';
import { setObjToUrlParams, deepMerge } from '/@/utils';
import { useI18n, useLocale } from '@mcex/locale';
import { joinTimestamp, formatRequestDate } from './helper';
import { useUserStoreWithOut } from '/@/store/modules/user';
import { AxiosRetry } from '/@/utils/http/axios/axiosRetry';
import { UserApi } from '/@/api/sys/user';
import { AUTH_PREFIX } from '/@/api/const';
import qs from 'qs';
import { confirmSinUp, logoutFn } from '/@/hooks/sys/useRefreshToken';
import { unref } from 'vue';
import * as Sentry from '@sentry/vue';
import type { Transaction, Span } from '@sentry/vue';
import { isDevMode } from '/@/utils/env';
import { CommonApi } from '/@/api/common';

const globSetting = useGlobSetting();
const urlPrefix = globSetting.urlPrefix;
const { createMessage, createErrorModal } = useMessage();
let transaction: Nullable<Transaction> = null;
let span: Nullable<Span> = null;
/**
 * 是否正在刷新的标记 -- 防止重复发出刷新token接口--节流阀
 */
let isRefreshing = false;

/**
 * @description: 数据处理，方便区分多种处理方式
 */
const transform: AxiosTransform = {
  /**
   * @description: 处理响应数据。如果数据不是预期格式，可直接抛出错误
   */
  transformResponseHook: (res: AxiosResponse<Result>, options: RequestOptions) => {
    /**
     * sentry上报
     */
    if (!isDevMode()) {
      try {
        // 提交 span
        span?.finish();
        // 提交事务
        transaction?.finish();
      } catch (error) {
        console.log('🚀 提交事务error:', error);
      }
    }

    const { t } = useI18n();
    const { isTransformResponse, isReturnNativeResponse } = options;
    // 是否返回原生响应头 比如：需要获取响应头时使用该属性
    if (isReturnNativeResponse) {
      return res;
    }
    // 不进行任何处理，直接返回
    // 用于页面代码可能需要直接获取code，data，message这些信息时开启
    if (!isTransformResponse) {
      return res.data;
    }
    // 错误的时候返回
    const { data } = res;
    if (!data) {
      // return '[HTTP] Request has no return value';
      throw new Error(t('sys.api.apiRequestFailed'));
    }
    //  这里 code，result，message为 后台统一的字段，需要在 types.ts内修改为项目自己的接口返回格式
    const { code, data: result, msg } = data;

    // 这里逻辑可以根据项目进行修改
    const hasSuccess =
      data &&
      Reflect.has(data, 'code') &&
      [ResultEnum.SUCCESS, ResultEnum.SUCCESS_IDX].includes(code);
    if (hasSuccess) {
      return result;
    }

    // 在此处根据自己项目的实际情况对不同的code执行不同的操作
    // 如果不希望中断当前请求，请return数据，否则直接抛出异常即可
    let timeoutMsg = '';
    switch (code) {
      case ResultEnum.TIMEOUT:
        timeoutMsg = t('sys.api.timeoutMessage');
        const userStore = useUserStoreWithOut();
        userStore.setToken(undefined);
        userStore.logout(true);
        break;
      case ResultEnum.REFRESH: {
        try {
          logoutFn(false, undefined);
        } catch (error) {}
        throw new Error(timeoutMsg || t('sys.api.apiRequestFailed'));
      }
      default:
        if (msg) {
          timeoutMsg = msg;
        }
    }

    // errorMessageMode=‘modal’的时候会显示modal错误弹窗，而不是消息提示，用于一些比较重要的错误
    // errorMessageMode='none' 一般是调用时明确表示不希望自动弹出错误提示
    if (options.errorMessageMode === 'modal') {
      createErrorModal({ title: t('sys.api.errorTip'), content: timeoutMsg });
    } else if (options.errorMessageMode === 'message') {
      createMessage.error(timeoutMsg);
    }

    throw new Error(timeoutMsg || t('sys.api.apiRequestFailed'));
  },

  // 请求之前处理config
  beforeRequestHook: (config, options) => {
    const { apiUrl, joinPrefix, joinParamsToUrl, formatDate, joinTime = true, urlPrefix } = options;

    if (joinPrefix) {
      config.url = `${urlPrefix}${config.url}`;
    }

    if (apiUrl && isString(apiUrl)) {
      config.url = `${apiUrl}${config.url}`;
    }
    const params = config.params || {};
    const data = config.data || false;
    formatDate && data && !isString(data) && formatRequestDate(data);
    if (config.method?.toUpperCase() === RequestEnum.GET) {
      if (!isString(params)) {
        // 给 get 请求加上时间戳参数，避免从缓存中拿数据。
        config.params = Object.assign(params || {}, joinTimestamp(joinTime, false));
      } else {
        // 兼容restful风格
        config.url = config.url + params + `${joinTimestamp(joinTime, true)}`;
        config.params = undefined;
      }
    } else {
      if (!isString(params)) {
        formatDate && formatRequestDate(params);
        if (
          Reflect.has(config, 'data') &&
          config.data &&
          (Object.keys(config.data).length > 0 || config.data instanceof FormData)
        ) {
          config.data = data;
          config.params = params;
        } else {
          // 非GET请求如果没有提供data，则将params视为data
          config.data = params;
          config.params = undefined;
        }
        if (joinParamsToUrl) {
          config.url = setObjToUrlParams(
            config.url as string,
            Object.assign({}, config.params, config.data),
          );
        }
      } else {
        // 兼容restful风格
        config.url = config.url + params;
        config.params = undefined;
      }
    }

    /**
     * sentry上报
     */
    if (!isDevMode()) {
      try {
        transaction = Sentry.startTransaction({ name: 'httpRequest' });
        // 将创建的事务附带到当前 sentry 上下文
        Sentry.getCurrentHub().configureScope((scope) => scope.setSpan(transaction!));
        // 创建一个 span 并塞到事务里，这里的 op 和 description 你可以都用接口名来做区分
        span = transaction.startChild({
          data: {
            // 你想附带的数据
          },
          op: config.url, // 操作名
          description: config.url, // 描述
        });
      } catch (error) {
        console.log('🚀 初始化事务 error:', error);
      }
    }
    return config;
  },

  /**
   * @description: 请求拦截器处理
   */
  requestInterceptors: (config, options) => {
    const { getLocale } = useLocale();

    // 请求之前处理config
    const userStore = useUserStoreWithOut();
    const token = userStore.getToken;
    if (token && (config as Recordable)?.requestOptions?.withToken !== false) {
      // jwt token
      (config as Recordable).headers.Authorization = options.authenticationScheme
        ? `${options.authenticationScheme}${token}`
        : token;
    }

    // 多语言传参
    (config as Recordable).headers['Accept-Language'] = unref(getLocale).replace(/_/g, '-');
    (config as Recordable).headers['platform'] = 'web';
    return config;
  },

  /**
   * @description: 响应拦截器处理
   */
  responseInterceptors: (res: AxiosResponse<any>) => {
    return res;
  },

  /**
   * @description: 响应错误处理
   */
  responseInterceptorsCatch: (axiosInstance: AxiosInstance, error: any) => {
    const { t } = useI18n();

    const { response, code, message, config } = error || {};
    const errorMessageMode = config?.requestOptions?.errorMessageMode || 'none';
    const msg: string = response?.data?.msg ?? '';
    const err: string = error?.toString?.() ?? '';
    let errMessage = '';

    try {
      if (code === 'ECONNABORTED' && message.indexOf('timeout') !== -1) {
        errMessage = t('sys.api.apiTimeoutMessage');
      }
      if (err?.includes('Network Error')) {
        errMessage = t('sys.api.networkExceptionMsg');
      }

      /**
       * sentry上报错误
       */
      Sentry.captureMessage(errMessage || '接口请求错误', {
        extra: { error }, // 附带的数据
        level: 'warning', // 错误等级，警告还是报错由你决定
      });

      if (errMessage) {
        if (errorMessageMode === 'modal') {
          createErrorModal({ title: t('sys.api.errorTip'), content: errMessage });
        } else if (errorMessageMode === 'message') {
          createMessage.error(errMessage);
        }
        return Promise.reject(error);
      }
    } catch (error) {
      throw new Error(error as unknown as string);
    }

    /**
     * 如果token过期则尝试刷新token
     * 刷新完成后重新请求
     */
    const isUnauthorizedError = error?.response?.status === ErrorCodeEnum.Unauthorized;
    const url = response.config.url;
    const isUrlExcluded = url.includes(UserApi.RefreshToken) || url.includes(CommonApi.features);

    if (isUnauthorizedError && !isUrlExcluded) {
      const userStore = useUserStoreWithOut();
      // 未进行获取
      if (!isRefreshing) {
        isRefreshing = true;
        // 设置刷新Token
        // 调用刷新接口
        userStore
          .refreshTokenAction()
          .then((res) => {
            isRefreshing = false;

            if (res) {
              // 重新获取数据
              userStore.doSubscriber();
            }
          })
          .catch(() => {
            isRefreshing = false;
            // 刷新token失败
            console.log('刷新Token失败');
            confirmSinUp(false, () => {
              userStore.logout(false);
            });
          });
      }
      // 并将请求缓存起来
      const retryRequest = new Promise((resolve) => {
        // 返回Promise并且让其状态一直为等待状态,
        // 只有当token刷新成功后, 就会调用通过addSubscriber函数添加的缓存接口,
        // 此时, Promise的状态就会变成resolve
        userStore.addSubscriber((newToken) => {
          // 表示用新的token去替换掉原来的token
          response.config.headers.Authorization = newToken;
          // 替换掉url -- 因为baseURL会扩展请求url
          // response.config.url = response.config.url.replace(response.config.baseURL, '');
          // 用重新封装的config去请求, 就会将重新请求后的返回
          // 记录重试次数
          const { count } = response.config?.requestOptions?.retryRequest;
          response.config.__retryCount = response.config.__retryCount || 0;
          if (response.config.__retryCount >= count) {
            return Promise.reject(error);
          }
          response.config.__retryCount += 1;
          resolve(axiosInstance(response.config));
        });
      });
      return retryRequest;
    }

    // 如果为特性接口401则刷新页面
    if (isUnauthorizedError && url.includes(CommonApi.features)) {
      const userStore = useUserStoreWithOut();
      userStore.logout(true);
      window.location.reload();
    }

    /**
     * 403 且code为9998 表示账号异常
     * 退出登录
     */
    if (
      error?.response?.status === ErrorCodeEnum.NoPermission &&
      response?.data?.code === ResultEnum.REFRESH
    ) {
      logoutFn(false, undefined);
      return Promise.reject(error);
    }
    /**
     * 403 且code为9999 表示账号异常
     * 退出登录
     */
    if (
      error?.response?.status === ErrorCodeEnum.NoPermission &&
      response?.data?.code === ResultEnum.LOGIN
    ) {
      const userStore = useUserStoreWithOut();
      userStore.logout(true);
      return Promise.reject(error);
    }
    checkStatus(error?.response?.status, msg, errorMessageMode);

    // 添加自动重试机制 保险起见 只针对GET请求
    const retryRequest = new AxiosRetry();
    const { isOpenRetry } = config?.requestOptions?.retryRequest ?? {};
    if (config?.method?.toUpperCase() === RequestEnum.GET && isOpenRetry) {
      // @ts-ignore
      retryRequest.retry(axiosInstance, error);
    }

    return Promise.reject(error);
  },
};
/**
 * 根据域名获取apiUrl
 */
export function getApiUrlByDomain(url: string) {
  const { apiUrlDev, apiUrlStg, apiUrlUat, apiUrlProd } = globSetting;
  // 创建一个新的 URL 对象
  const urlObj = new URL(url);
  const environments = {
    localhost: apiUrlDev,
    'exmt-dev1.microconnect.cc': apiUrlDev,
    'mtex-stg2.microconnect.cc': apiUrlStg,
    'exmt-uat1.microconnect.cc': apiUrlUat,
    'mt.microconnect.cc': apiUrlProd,
  };
  return environments[urlObj.hostname] || globSetting.apiUrl;
}
function createAxios(opt?: Partial<CreateAxiosOptions>) {
  return new VAxios(
    // 深度合并
    deepMerge(
      {
        // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#authentication_schemes
        // authentication schemes，e.g: Bearer
        // authenticationScheme: 'Bearer',
        authenticationScheme: '',
        timeout: 10 * 1000,
        // 基础接口地址
        // baseURL: globSetting.apiUrl,

        headers: { 'Content-Type': ContentTypeEnum.JSON },
        // 如果是form-data格式
        // headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
        // 数据处理方式
        transform: clone(transform),
        // 配置项，下面的选项都可以在独立的接口请求中覆盖
        requestOptions: {
          // 默认将prefix 添加到url
          joinPrefix: true,
          // 是否返回原生响应头 比如：需要获取响应头时使用该属性
          isReturnNativeResponse: false,
          // 需要对返回数据进行处理
          isTransformResponse: true,
          // post请求的时候添加参数到url
          joinParamsToUrl: false,
          // 格式化提交参数时间
          formatDate: true,
          // 消息提示类型
          errorMessageMode: 'message',
          // 接口地址
          apiUrl: getApiUrlByDomain(window.location.href),
          // 接口拼接地址
          urlPrefix: urlPrefix,
          //  是否加入时间戳
          joinTime: false,
          // 忽略重复请求
          ignoreCancelToken: true,
          // 是否携带token
          withToken: true,
          retryRequest: {
            isOpenRetry: true,
            count: 5,
            waitTime: 100,
          },
        },
      },
      opt || {},
    ),
  );
}

export const defHttp = createAxios({
  authenticationScheme: AUTH_PREFIX,
  paramsSerializer: function (params) {
    return qs.stringify(params, { arrayFormat: 'comma' });
  },
});

// other api url
// export const otherHttp = createAxios({
//   requestOptions: {
//     apiUrl: 'xxx',
//     urlPrefix: 'xxx',
//   },
// });
